import React, { useEffect, Component } from 'react';
import './App.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation
} from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardRouter from "./components/DashboardRouter";
import DesktopBlocker from "./components/DesktopBlocker";

import Main from "./Main";
import SignUp from "./Screen/SignUp";
import SignIn from "./Screen/SignIn";
import ForgotPassword from "./Screen/ForgotPassword";
import ResetPassword from "./Screen/ResetPassword";
import Welcome from "./Screen/Welcome";
import Wallet from "./Screen/Wallet";
import AcountStatement from "./Screen/AcountStatement";
import Notifications from "./Screen/Notifications";
import MarketTrends from "./Screen/MarketTrends";
import Referral from "./Screen/Referral";
import UserProfile from "./Screen/UserProfile";
import AdminDashboard from "./Screen/AdminDashboard";
import AdminUsers from "./Screen/AdminUsers";
import AdminUserDetail from "./Screen/AdminUserDetail";
import AdminSignIn from "./Screen/AdminSignIn";
import AdminProfile from "./Screen/AdminProfile";
import AdminReferrals from "./Screen/AdminReferrals";
import AdminReferralDetail from "./Screen/AdminReferralDetail";
import AdminSettings from "./Screen/AdminSettings";
import NotFound from "./Screen/NotFound";
import Footer from "./Include/Footer";

class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('App error boundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh',
          background: '#0e0758', color: '#fff', padding: '24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ marginBottom: 8, fontSize: 20 }}>Something went wrong</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 24, fontSize: 14 }}>
            An unexpected error occurred. Please refresh the page.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{
              background: '#19bfb1', border: 'none', borderRadius: 12,
              color: '#fff', padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Component to handle layout class based on route
function LayoutManager({ children }) {
  const location = useLocation();

  useEffect(() => {
    const allWrapper = document.querySelector('.allWrapper');
    if (allWrapper) {
      allWrapper.classList.remove('admin-layout', 'mobile-only-layout', 'landing-layout');

      if (location.pathname.startsWith('/admin')) {
        allWrapper.classList.add('admin-layout');
      } else if (location.pathname === '/') {
        allWrapper.classList.add('landing-layout');
      } else {
        allWrapper.classList.add('mobile-only-layout');
      }
    }
  }, [location]);

  return children;
}

function App() {
  return (
    <div className='allWrapper'>
      <AuthProvider>
        <Router>
          <LayoutManager>
            <Footer />
            <ErrorBoundary>
              <Routes>
                {/* Public Routes */}
                <Route exact path='/' element={<Main/>} />
                <Route path='/sign-up' element={<DesktopBlocker><SignUp/></DesktopBlocker>} />
                <Route path='/sign-in' element={<DesktopBlocker><SignIn/></DesktopBlocker>} />
                <Route path='/admin/login' element={<AdminSignIn/>} />
                <Route path='/forgot-password' element={<DesktopBlocker><ForgotPassword/></DesktopBlocker>} />
                <Route path='/reset-password' element={<DesktopBlocker><ResetPassword/></DesktopBlocker>} />

                {/* Dashboard Router - redirects to role-specific dashboard */}
                <Route path='/dashboard' element={<ProtectedRoute><DashboardRouter/></ProtectedRoute>} />

                {/* Protected User Routes */}
                <Route path='/welcome' element={<ProtectedRoute><Welcome/></ProtectedRoute>} />
                <Route path='/wallet' element={<ProtectedRoute><Wallet/></ProtectedRoute>} />
                <Route path='/account-statement' element={<ProtectedRoute><AcountStatement/></ProtectedRoute>} />
                <Route path='/notifications' element={<ProtectedRoute><Notifications/></ProtectedRoute>} />
                <Route path='/market-trends' element={<ProtectedRoute><MarketTrends/></ProtectedRoute>} />
                <Route path='/referral' element={<ProtectedRoute><Referral/></ProtectedRoute>} />
                <Route path='/profile' element={<ProtectedRoute><UserProfile/></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path='/admin' element={<ProtectedRoute><AdminDashboard/></ProtectedRoute>} />
                <Route path='/admin/users' element={<ProtectedRoute><AdminUsers/></ProtectedRoute>} />
                <Route path='/admin/users/:userId' element={<ProtectedRoute><AdminUserDetail/></ProtectedRoute>} />
                <Route path='/admin/profile' element={<ProtectedRoute><AdminProfile/></ProtectedRoute>} />
                <Route path='/admin/referrals' element={<ProtectedRoute><AdminReferrals/></ProtectedRoute>} />
                <Route path='/admin/referrals/:userId' element={<ProtectedRoute><AdminReferralDetail/></ProtectedRoute>} />
                <Route path='/admin/settings' element={<ProtectedRoute><AdminSettings/></ProtectedRoute>} />

                {/* Catch-all. Without this an unmatched path rendered a blank
                    page with only the bottom nav and no way back. Deliberately
                    not wrapped in DesktopBlocker or ProtectedRoute: a 404 must
                    render on every device and whether or not you are signed in. */}
                <Route path='*' element={<NotFound/>} />
              </Routes>
            </ErrorBoundary>
          </LayoutManager>
        </Router>
      </AuthProvider>
    </div>
  );
}

export default App;
